import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { TenantContextService } from "../services/tenant-context.service";

/**
 * 2026-05-06 G+3 — Tenant-admin route guard. Mirrors SuperAdminGuard
 * for tenant-level admin surfaces (pricing, branding, role mgmt,
 * feature flags, mobile-page-config). Provides route-level
 * defense-in-depth so non-admin roles can't even *navigate* to a
 * tenant-admin page (UI gates handle in-page rendering, but this
 * blocks the URL itself).
 *
 * Allowed roles:
 *   - tenant_admin (full tenant ops + governance)
 *   - super_admin (multi-tenant deploy with cross-tenant impersonation)
 *
 * Denied roles:
 *   - tenant_manager (operations only — courses + maqraah + teacher
 *     assignment, NO pricing / branding / role mgmt)
 *   - supervisor (mobile listener — no admin web access at all)
 *   - teacher / student / parent (non-admin)
 *
 * Same race-handling pattern as SuperAdminGuard: await
 * TenantContextService.load() if memberships haven't resolved before
 * the first navigation.
 */
export const TenantAdminGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const tenantContext = inject(TenantContextService);

  if (tenantContext.memberships().length === 0 && !tenantContext.active()) {
    try {
      await tenantContext.load();
    } catch {
      /* fall through to deny below */
    }
  }

  if (tenantContext.isTenantAdmin() || tenantContext.isSuperAdmin()) return true;

  router.navigateByUrl("/home");
  return false;
};
